import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useAddWhitelist } from '../api';

export function AddWhitelistPage() {
  const navigate = useNavigate();
  const add = useAddWhitelist();
  const [mobile, setMobile] = useState('');
  const canWhitelist = usePermission('Number Action.Add Whitelist', BASE_PERMISSION.ADD);

  if (!canWhitelist) return <Navigate to="/view-whitelist" replace />;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = mobile.trim();
    if (!/^\d{10}$/.test(trimmed)) {
      toast.error('Enter a valid 10-digit mobile number.');
      return;
    }
    add.mutate(trimmed, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'Added to whitelist');
        navigate('/view-whitelist');
      },
      onError: () => toast.error('Could not add to whitelist.'),
    });
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Add to Whitelist"
        description="Allow a mobile number to bypass the platform's DND and blacklist guard."
        actions={
          <Button asChild variant="outline">
            <Link to="/view-whitelist">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Whitelist entry</CardTitle>
          <CardDescription>
            Enter the 10-digit mobile number you want to whitelist.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-4 sm:max-w-sm">
            <div className="grid gap-1.5">
              <Label htmlFor="mobile">
                Mobile<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="mobile"
                inputMode="numeric"
                maxLength={10}
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))
                }
                placeholder="10-digit mobile number"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={add.isPending}>
                {add.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Add to whitelist
              </Button>
              <Button asChild type="button" variant="ghost">
                <Link to="/view-whitelist">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
